import React from "react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { FileDown, FileText, ScrollText, ClipboardCheck, MapPin } from "lucide-react";
import { toast } from "sonner";
import API from "../../lib/api";
import { SERVICE_TYPES, REQUEST_TYPES, labelOf } from "../../lib/gmaoConstants";
import { QUOTE_STATUS_META } from "../../lib/quoteConstants";
import { INVOICE_STATUS_META, fmtEUR } from "../../lib/invoiceConstants";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Separator } from "../ui/separator";
import StatusBadge from "./StatusBadge";
import PhotoThumb from "./PhotoThumb";

const fmtDate = (iso) => {
  try { return format(new Date(iso), "dd/MM/yyyy", { locale: fr }); } catch { return "-"; }
};

const ClientInterventionDetail = ({ open, onOpenChange, intervention, quote, invoice, onAfterDownload }) => {
  if (!intervention) return null;
  const hasBon = intervention.report || intervention.signature;

  const dl = async (url) => {
    try {
      const res = await API.get(url, { responseType: "blob" });
      const u = window.URL.createObjectURL(res.data);
      window.open(u, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(u), 10000);
      onAfterDownload?.();
    } catch {
      toast.error("Téléchargement impossible.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="client-intervention-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 flex-wrap">
            <span className="font-mono text-[hsl(207,78%,42%)]">{intervention.dossierNumber}</span>
            <StatusBadge status={intervention.status} />
          </DialogTitle>
          <DialogDescription>Suivi de votre dossier et documents associés.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <div>
            <p className="font-medium text-slate-800">{labelOf(REQUEST_TYPES, intervention.requestType)} · {labelOf(SERVICE_TYPES, intervention.serviceType)}</p>
            <p className="text-slate-600 mt-1">{intervention.description}</p>
            <p className="flex items-center gap-1 text-xs text-muted-foreground mt-2">
              <MapPin className="w-3.5 h-3.5" /> {intervention.address} {intervention.postalCode} {intervention.city}
            </p>
            <p className="text-xs text-muted-foreground">Créée le {fmtDate(intervention.createdAt)}{intervention.preferredSlot ? ` · Créneau : ${intervention.preferredSlot}` : ""}</p>
          </div>

          {/* Documents associés */}
          <Separator />
          <p className="font-semibold text-slate-800">Documents</p>

          {/* Devis */}
          <div className="flex items-center justify-between bg-slate-50 rounded-lg p-3" data-testid="doc-quote">
            <div className="flex items-center gap-2">
              <ScrollText className="w-4 h-4 text-[hsl(207,78%,42%)]" />
              <div>
                <p className="font-medium">Devis {quote?.number || ""}</p>
                {quote ? (
                  <span className={`text-[11px] px-2 py-0.5 rounded border ${QUOTE_STATUS_META[quote.status]?.color || ""}`}>{QUOTE_STATUS_META[quote.status]?.label || quote.status}</span>
                ) : <span className="text-xs text-muted-foreground">Aucun devis</span>}
              </div>
            </div>
            {quote && (
              <Button variant="outline" size="sm" onClick={() => dl(`/quotes/${quote.id}/pdf`)} data-testid="dl-quote">
                <FileDown className="w-4 h-4 mr-1" /> PDF
              </Button>
            )}
          </div>

          {/* Facture */}
          <div className="flex items-center justify-between bg-slate-50 rounded-lg p-3" data-testid="doc-invoice">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-[hsl(207,78%,42%)]" />
              <div>
                <p className="font-medium">Facture {invoice?.number || ""}</p>
                {invoice ? (
                  <span className={`text-[11px] px-2 py-0.5 rounded border ${INVOICE_STATUS_META[invoice.status]?.color || ""}`}>{INVOICE_STATUS_META[invoice.status]?.label || invoice.status} · {fmtEUR(invoice.totalTTC)}</span>
                ) : <span className="text-xs text-muted-foreground">Aucune facture</span>}
              </div>
            </div>
            {invoice && (
              <Button variant="outline" size="sm" onClick={() => dl(`/invoices/${invoice.id}/pdf`)} data-testid="dl-invoice">
                <FileDown className="w-4 h-4 mr-1" /> PDF
              </Button>
            )}
          </div>

          {/* Bon d'intervention */}
          <div className="flex items-center justify-between bg-slate-50 rounded-lg p-3" data-testid="doc-bon">
            <div className="flex items-center gap-2">
              <ClipboardCheck className="w-4 h-4 text-[hsl(207,78%,42%)]" />
              <div>
                <p className="font-medium">Bon d&apos;intervention</p>
                {intervention.signature ? (
                  <span className="text-xs text-emerald-700">Signé par {intervention.signature.signerName}</span>
                ) : hasBon ? (
                  <span className="text-xs text-muted-foreground">Rapport disponible</span>
                ) : (
                  <span className="text-xs text-muted-foreground">Pas encore disponible</span>
                )}
              </div>
            </div>
            {hasBon && (
              <Button variant="outline" size="sm" onClick={() => dl(`/interventions/${intervention.id}/report-pdf`)} data-testid="dl-bon">
                <FileDown className="w-4 h-4 mr-1" /> PDF
              </Button>
            )}
          </div>

          {/* Rapport */}
          {intervention.report && (
            <>
              <Separator />
              <div className="space-y-1">
                <p className="font-semibold text-slate-800">Rapport d&apos;intervention</p>
                {intervention.report.workDone && <p><span className="text-muted-foreground">Travail réalisé :</span> {intervention.report.workDone}</p>}
                {intervention.report.partToOrder && <p><span className="text-muted-foreground">Pièce à commander :</span> {intervention.report.partToOrder}</p>}
                {intervention.report.clientObservation && <p><span className="text-muted-foreground">Observation :</span> {intervention.report.clientObservation}</p>}
              </div>
            </>
          )}

          {/* Photos */}
          {(intervention.photos || []).length > 0 && (
            <>
              <Separator />
              <div>
                <p className="font-semibold text-slate-800 mb-2">Photos</p>
                <div className="grid grid-cols-4 gap-2">
                  {intervention.photos.map((p) => (
                    <div key={p.id} className="relative">
                      <PhotoThumb interventionId={intervention.id} photo={p} className="w-full h-20 rounded-lg border" />
                      <span className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[9px] text-center rounded-b-lg">{p.category}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Historique */}
          <Separator />
          <div>
            <p className="font-semibold text-slate-800 mb-2">Historique du dossier</p>
            <div className="space-y-1">
              {(intervention.statusHistory || []).slice().reverse().map((h, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <span className="text-muted-foreground">{(h.at || "").slice(0, 16).replace("T", " ")}</span>
                  <StatusBadge status={h.status} />
                  {h.note && <span className="text-slate-600">— {h.note}</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ClientInterventionDetail;
